import React from "react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import { MapPin, ShoppingBag } from "lucide-react"
import { useLanguage } from "@/hooks/useLanguage"
import { formatUnitsBreakdown, formatTotalWeight, formatCurrency } from "@/lib/productDisplay"
import { ivaAmount } from "@/lib/tax"
import { cn } from "@/lib/utils"

interface ReviewSidebarProps {
  items: any[]
  total: number
  text: any
  navigate: (path: string) => void
  getValues: (field: string) => string
}

export const ReviewSidebar: React.FC<ReviewSidebarProps> = ({
  items,
  total: _ignoredTotal, // We calculate it internally for accuracy
  text,
  navigate,
  getValues,
}) => {
  const { t } = useLanguage()
  const shouldConstrainItems = items.length > 4
  const totalPrice = items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0)
  const iva = items.reduce((sum, item) => {
    return sum + (item.isTaxable ? ivaAmount((item.price || 0) * item.quantity) : 0)
  }, 0)
  const total = totalPrice + iva

  return (
    <>
      <div className="overflow-hidden rounded-lg border-l-[1px] border-accent bg-foreground/10 p-px shadow-2xl">
        <div className="space-y-8 bg-card/95 p-10 backdrop-blur-md">
          <div className="flex items-center gap-4 text-accent">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            <h2 className="font-display text-3xl uppercase tracking-widest">{text.paymentInProgress}</h2>
          </div>
          <p className="text-xs font-bold text-muted-foreground">
            {text.paymentInstructions}
          </p>
          <Button 
            variant="outline" 
            onClick={() => navigate("/catalog/api")}
            className="w-full font-mono text-lg tracking-widest hover:bg-accent/80"
          >
            {text.cancelRestart}
          </Button>
        </div>
      </div>

      {/* Shipping Review Summary */}
      <div className="rounded-lg border-[1px] border-border bg-muted/30 p-8 space-y-6">
        <div className="flex items-center gap-2 border-b border-foreground/5 pb-3">
          <MapPin className="h-4 w-4 text-accent" />
          <h3 className="font-display text-xl uppercase tracking-widest">{text.shippingTo}</h3>
        </div>
        <div className="space-y-4">
          <div>
            <p className="font-display text-xl tracking-wider text-foreground">{getValues("receiverName")}</p>
            <p className="text-xs font-bold text-muted-foreground">{getValues("phone")}</p>
          </div>
          <p className="text-xs font-bold uppercase text-muted-foreground/80">
            {getValues("street")} {getValues("externalNumber")} {getValues("internalNumber") && `Int. ${getValues("internalNumber")}`}<br />
            {getValues("neighborhood")}, {getValues("city")}<br />
            {getValues("state")}, {getValues("zipCode")}
          </p>
        </div>
      </div>

      {/* Order Details Summary */}
      <div className="rounded-lg border-[1px] border-border bg-foreground/[0.02] p-8 space-y-6">
        <div className="flex items-center justify-between border-b border-foreground/5 pb-3">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-accent" />
            <h3 className="font-display text-xl uppercase tracking-widest">{text.orderSummary}</h3>
          </div>
          <span className="font-mono text-xs font-bold text-accent uppercase tracking-widest">{items.length} {text.items}</span>
        </div>
        <div className="space-y-3">
          <ScrollArea
            data-testid="checkout-review-items"
            className={cn(
              "pr-1",
              shouldConstrainItems ? "h-[clamp(12rem,30vh,22rem)]" : "max-h-[30vh]"
            )}
          >
            <div className="space-y-3 pr-4">
              {items.map((item) => (
                <div key={item.id} className="flex justify-between items-start text-xs font-bold uppercase tracking-wider text-muted-foreground/80">
                  <div className="flex flex-col">
                    <span>{item.name}</span>
                    <span className="text-[10px] text-muted-foreground/60">
                      {formatUnitsBreakdown(item.quantity, item.weight, item.weightUnit, t.units)}
                      {item.weight && item.weight > 0 && (
                        <> · {formatTotalWeight(item.quantity, item.weight, item.weightUnit, t.units.total)}</>
                      )}
                    </span>
                  </div>
                  <span className="font-mono tabular-nums">{formatCurrency((item.price || 0) * item.quantity)}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
          <Separator className="bg-foreground/5 my-4" />
          <div className="flex justify-between items-end">
            <span className="font-display text-xl uppercase tracking-widest">{text.totalAmount}</span>
            <span className="font-display text-2xl text-accent">{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </>
  )
}
